/* NOIRVA delivery estimate — production + regional shipping window combined. */
(() => {
  const dateKey = (date) => date.toISOString().slice(0, 10);

  function parseDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) {
      throw new Error('Date must use YYYY-MM-DD format.');
    }

    const date = new Date(`${value}T00:00:00Z`);
    if (!Number.isFinite(+date) || dateKey(date) !== value) {
      throw new Error('Invalid date.');
    }
    return date;
  }

  function createWorkdayCalendar(week, holidays) {
    const workdays = String(week)
      .split(',')
      .map((value) => Number(value.trim()));

    if (!workdays.length || workdays.some((day) => !Number.isInteger(day) || day < 0 || day > 6)) {
      throw new Error('Workweek must contain weekday numbers 0–6, separated by commas.');
    }

    const excludedDates = new Set(
      String(holidays || '')
        .split(/[\s,]+/)
        .filter(Boolean)
        .map((value) => dateKey(parseDate(value)))
    );

    return (date) => workdays.includes(date.getUTCDay()) && !excludedDates.has(dateKey(date));
  }

  function advanceBusinessDays(startDate, days, isWorkday, includeStart = false) {
    if (!Number.isInteger(days) || days < 0 || days > 730) {
      throw new Error('Day count must be an integer between 0 and 730.');
    }

    const result = new Date(startDate);
    let remaining = days;

    if (includeStart && remaining > 0 && isWorkday(result)) {
      remaining -= 1;
    }

    let guard = 0;
    while (remaining > 0) {
      result.setUTCDate(result.getUTCDate() + 1);
      if (isWorkday(result)) remaining -= 1;
      if (++guard > 4000) throw new Error('Unable to calculate with the current workweek settings.');
    }

    return result;
  }

  function getLocalTime(now, timeZone) {
    if (!timeZone || !timeZone.trim()) {
      throw new Error('Enter the IANA time zone used by your Shopify store.');
    }

    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-CA', {
        timeZone: timeZone.trim(),
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      })
        .formatToParts(now)
        .map((part) => [part.type, part.value])
    );

    return {
      date: parseDate(`${parts.year}-${parts.month}-${parts.day}`),
      seconds: Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second),
    };
  }

  function parseCutoff(value) {
    if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(value || '')) {
      throw new Error('Cutoff time must use 24-hour HH:MM format.');
    }

    const [hours, minutes] = value.split(':').map(Number);
    return hours * 3600 + minutes * 60;
  }

  function calculateRanges(config, now = new Date()) {
    if (config.mode === 'manual') {
      return ['standard', 'expedited'].map((type) => {
        const start = parseDate(config[`${type}_start`]);
        const end = parseDate(config[`${type}_end`]);
        if (+end < +start) throw new Error('End date cannot be earlier than start date.');
        return [start, end];
      });
    }

    const isWorkday = createWorkdayCalendar(config.workweek, config.holidays);
    const local = getLocalTime(now, config.timezone);
    const cutoff = config.use_cutoff ? parseCutoff(config.cutoff) : null;
    const eligibleDate = new Date(local.date);

    if (cutoff !== null && local.seconds >= cutoff) {
      eligibleDate.setUTCDate(eligibleDate.getUTCDate() + 1);
    }

    let guard = 0;
    while (!isWorkday(eligibleDate)) {
      eligibleDate.setUTCDate(eligibleDate.getUTCDate() + 1);
      if (++guard > 4000) throw new Error('No valid workday is available with the current settings.');
    }

    return ['standard', 'expedited'].map((type) => {
      const min = Number(config[`${type}_min`]);
      const max = Number(config[`${type}_max`]);

      if (!Number.isInteger(min) || !Number.isInteger(max) || min < 1 || max < min) {
        throw new Error('Delivery window is invalid.');
      }

      return [
        advanceBusinessDays(eligibleDate, min, isWorkday, Boolean(config.count_today)),
        advanceBusinessDays(eligibleDate, max, isWorkday, Boolean(config.count_today)),
      ];
    });
  }

  if (typeof window === 'undefined' || customElements.get('noirva-delivery')) return;

  class NoirvaDelivery extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);

      const { signal } = this.abortController;
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.update();
      }, { signal });

      this.update();
      clearInterval(this.refreshTimer);
      this.refreshTimer = window.setInterval(() => {
        if (!document.hidden) this.update();
      }, 60_000);
    }

    disconnectedCallback() {
      clearInterval(this.refreshTimer);
      this.abortController?.abort();
    }

    update() {
      const error = this.querySelector('[data-error]');

      try {
        const now = new Date();
        const ranges = calculateRanges(this.config, now);
        const showYear = this.config.show_year || ranges.some((range) =>
          range.some((date) => date.getUTCFullYear() !== now.getUTCFullYear())
        );
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: 'UTC',
          month: this.config.month_format,
          day: 'numeric',
          ...(showYear ? { year: 'numeric' } : {}),
        });

        this.querySelectorAll('[data-date]').forEach((element, index) => {
          const [start, end] = ranges[index];
          const value = +start === +end
            ? formatter.format(start)
            : `${formatter.format(start)} – ${formatter.format(end)}`;

          if (element.textContent !== value) element.textContent = value;
        });

        if (error) {
          error.hidden = true;
          error.textContent = '';
        }
      } catch (exception) {
        this.querySelectorAll('[data-date]').forEach((element) => {
          element.textContent = this.config.fallback;
        });

        if (error) {
          error.hidden = false;
          error.textContent = `Delivery settings: ${exception.message}`;
        }
      }
    }
  }

  customElements.define('noirva-delivery', NoirvaDelivery);
})();
